import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
  ResolvedEndUserThemeSchema,
  UpdateEndUserThemeBodySchema,
} from './end-user-theme.schema'

const c = initContract()

const prefix = '/v2/customers' as const

/**
 * Customer Settings Response Schema
 * Matches CustomerSettings from customer-settings.schema.ts
 */
export const CustomerSettingsResponseSchema = z.object({
  availableFontNames: z.array(z.string()),
  colors: z.record(z.string(), z.string()),
  endUserUrl: z.string(),
  logoUrl: z.string().optional(),
  backgroundUrl: z.string().optional(),
  fontFamily: z.string().optional(),
  fontFiles: z
    .array(z.object({ key: z.string(), weight: z.string() }))
    .optional(),
  portalColors: z
    .object({
      primary: z.string().optional(),
      background: z.string().optional(),
      foreground: z.string().optional(),
      cardBackground: z.string().optional(),
      cardForeground: z.string().optional(),
      button: z.string().optional(),
    })
    .optional(),
  validityDays: z.number().optional(),
  slug: z.string().optional(),
})

export type CustomerSettingsResponse = z.infer<
  typeof CustomerSettingsResponseSchema
>

const CustomerListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string(),
})

export const UpdateCustomerSettingsBodySchema = z.object({
  availableFontNames: z.array(z.string()).optional(),
  colors: z.record(z.string(), z.string()).optional(),
  endUserUrl: z.string().optional(),
  template: z.string().optional(),
  logoUrl: z.string().nullable().optional(),
  backgroundUrl: z.string().nullable().optional(),
  fontFamily: z.string().nullable().optional(),
  fontFiles: z
    .array(z.object({ key: z.string(), weight: z.string() }))
    .nullable()
    .optional(),
  portalColors: z
    .object({
      primary: z.string().optional(),
      background: z.string().optional(),
      foreground: z.string().optional(),
      cardBackground: z.string().optional(),
      cardForeground: z.string().optional(),
      button: z.string().optional(),
    })
    .nullable()
    .optional(),
  validityDays: z.number().int().positive().nullable().optional(),
  slug: z.string().regex(/^[a-z0-9-]+$/),
})

// Route order matters: literal-segment paths must be declared before
// param-segment paths within the same HTTP method to keep route matching
// deterministic across environments.
export const customer = c.router({
  // ----- GET -----
  getAll: {
    method: 'GET',
    path: prefix,
    responses: {
      200: z.array(CustomerListItemSchema),
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'List all customers',
    description: 'Returns all customers. Superadmin only.',
  },

  settings: {
    method: 'GET',
    path: `${prefix}/:customerId/settings`,
    pathParams: z.object({
      customerId: z.string().optional(),
    }),
    responses: {
      200: CustomerSettingsResponseSchema,
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get customer settings',
    description:
      'Returns customer-specific settings including available fonts, colors (CSS variable names as keys), and end-user URL. Superadmins can specify customerId to get settings for any customer.',
  },

  getEndUserTheme: {
    method: 'GET',
    path: `${prefix}/:customerId/end-user-theme`,
    pathParams: z.object({
      customerId: z.string(),
    }),
    responses: {
      200: ResolvedEndUserThemeSchema,
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get the resolved end-user theme of a customer',
    description:
      'Stored end-user theme merged over the defaults, with public URLs. Superadmin only.',
  },

  portalTranslations: {
    method: 'GET',
    path: `${prefix}/:customerId/portal-translations`,
    pathParams: z.object({
      customerId: z.string(),
    }),
    query: z.object({
      lang: z.string().optional(),
    }),
    responses: {
      200: z.object({
        translations: z.record(z.string(), z.any()),
        languages: z.array(z.string()),
      }),
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get portal translations for a customer',
    description:
      'Returns DB-resolved portal translations for all configured languages. Used by the admin theme preview.',
  },

  // ----- PUT -----
  updateSettings: {
    method: 'PUT',
    path: `${prefix}/:customerId/settings`,
    pathParams: z.object({
      customerId: z.string(),
    }),
    body: UpdateCustomerSettingsBodySchema,
    responses: {
      200: CustomerSettingsResponseSchema,
      403: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
      409: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Update customer settings',
    description: 'Update settings for a specific customer. Superadmin only.',
  },

  updateEndUserTheme: {
    method: 'PUT',
    path: `${prefix}/:customerId/end-user-theme`,
    pathParams: z.object({
      customerId: z.string(),
    }),
    body: UpdateEndUserThemeBodySchema,
    responses: {
      200: ResolvedEndUserThemeSchema,
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Update the end-user theme of a customer',
    description:
      'Upserts the one end-user theme row of the customer. Storage keys, not URLs. Superadmin only.',
  },

  upsertPortalTranslations: {
    method: 'PUT',
    path: `${prefix}/:customerId/portal-translations`,
    pathParams: z.object({
      customerId: z.string(),
    }),
    body: z.object({
      lang: z.string(),
      translations: z.array(z.object({ key: z.string(), value: z.string() })),
    }),
    responses: {
      200: z.object({ updated: z.number() }),
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Upsert portal translations for a customer',
    description:
      'Creates or updates portal translation overrides. If value matches file default or is empty, the row is deleted. Superadmin only.',
  },

  // ----- POST -----
  sendTestEmail: {
    method: 'POST',
    path: `${prefix}/:customerId/send-test-email`,
    pathParams: z.object({
      customerId: z.string(),
    }),
    body: z.object({
      email: z.string().email(),
    }),
    responses: {
      200: z.object({ sent: z.boolean() }),
      403: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Send a test gift portal email',
    description:
      'Sends a test gift-portal-confirmation email with mock data using the customer saved theme. Superadmin only.',
  },
})
